import React from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Controller, useForm } from "react-hook-form";
import {
  Button,
  FormControlLabel,
  FormHelperText,
  Grid,
  Switch,
} from "@mui/material";

import AppForm from "components/Shared/AppForm";
import InnerShadowBox from "components/Shared/InnerShadowBox";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";

import { useLazyGetDDLAuthorizationListQuery } from "store/services/authorizationApi";
import { useUpdateUserEntityAccessMutation } from "store/services/userApi";
import { useAuth } from "hooks/useAuth";

const UserEntityAccessForm = ({
  userEntity,
  disableEntityChange = false,
  onCancel = () => {},
}) => {
  const [defaultAuthorizationOptions, setDefaultAuthorizationOptions] =
    React.useState([]);

  const { id } = useParams();

  const { viewEntity } = useAuth();

  // react form hook
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm({
    defaultValues: {
      authorizations: [],
      defaultAuthorization: "",
      isActive: true,
    },
  });

  // RTK Query
  const [
    fetchAuthorizations,
    {
      data: authorizationList,
      isLoading: loadingAuthorizations,
      isFetching: fetchingAuthorizations,
    },
  ] = useLazyGetDDLAuthorizationListQuery();

  const [updateUserEntityAccess, { isLoading: updatingEntityUser }] =
    useUpdateUserEntityAccessMutation();

  const mappedAuthorizationOptions = React.useMemo(
    () =>
      authorizationList?.map((a) => ({
        label: a.role,
        id: a._id,
      })) ?? [],
    [authorizationList]
  );

  // methods
  const handleUpdateEntityAccess = async (data) => {
    try {
      await updateUserEntityAccess({
        id,
        entity: userEntity?.entity?._id,
        authorizations: data?.authorizations?.map((el) => el?.id),
        defaultAuthorization: data?.defaultAuthorization?.id,
        isActive: data?.isActive,
      }).unwrap();
      toast.success("Entity Access successfully updated!");
      onCancel();
    } catch (err) {
      toast.error(
        err?.data?.message ?? err?.message ?? "Couldn't update Entity's Access"
      );
    }
  };

  React.useEffect(() => {
    if (!userEntity) return;

    const authorizations = userEntity?.authorizations?.map((el) => ({
      label: el?.role,
      id: el?._id,
    }));

    setDefaultAuthorizationOptions(authorizations);

    const defaultAuthorization = userEntity?.defaultAuthorization
      ? {
          label: userEntity?.defaultAuthorization?.role,
          id: userEntity?.defaultAuthorization?._id,
        }
      : "";

    reset({
      isActive: userEntity?.isActive,
      authorizations,
      defaultAuthorization,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEntity]);

  React.useEffect(() => {
    if (!viewEntity?.authorization) return;

    let levels = [];
    if (viewEntity?.authorization?.level === "Entity") levels = ["Entity"];
    if (viewEntity?.authorization?.level === "Account")
      levels = ["Entity", "Account"];

    fetchAuthorizations({ levels });
  }, [fetchAuthorizations, viewEntity?.authorization]);

  return (
    <>
      <AppForm loading={updatingEntityUser}>
        <InnerShadowBox padding={3}>
          <Grid container spacing={5}>
            <Grid item xs={12}>
              <Controller
                name="authorizations"
                control={control}
                rules={{
                  required: "Please select at least one authorization.",
                }}
                render={({ field }) => (
                  <BaseAutocomplete
                    required
                    fullWidth
                    multiple
                    size="small"
                    label="Authorizations"
                    value={field?.value ?? []}
                    options={mappedAuthorizationOptions}
                    onChange={(_, authorizations) => {
                      setValue("defaultAuthorization", "");
                      setValue("authorizations", authorizations);
                      setDefaultAuthorizationOptions(authorizations);
                    }}
                    loading={loadingAuthorizations || fetchingAuthorizations}
                    loadingText="Loading Authorizations..."
                    limitTags={2}
                    filterSelectedOptions
                    disabled={disableEntityChange}
                  />
                )}
              />

              {!!errors.authorizations && (
                <FormHelperText error>
                  {errors.authorizations.message}
                </FormHelperText>
              )}
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="defaultAuthorization"
                control={control}
                rules={{
                  required: "Please select a default authorization",
                }}
                render={({ field }) => (
                  <BaseAutocomplete
                    required
                    fullWidth
                    label="Default Authorization"
                    options={defaultAuthorizationOptions}
                    value={field?.value ?? ""}
                    onChange={(_, defaultAuthorization) => {
                      setValue("defaultAuthorization", defaultAuthorization);
                    }}
                    disabled={disableEntityChange}
                  />
                )}
              />
              {!!errors.defaultAuthorization && (
                <FormHelperText error>
                  {errors.defaultAuthorization.message}
                </FormHelperText>
              )}
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        {...field}
                        checked={field.value}
                        value={field?.value}
                      />
                    }
                    label="Is Active?"
                  />
                )}
              />
            </Grid>
          </Grid>
        </InnerShadowBox>

        <AppForm.Actions>
          <Button disabled={updatingEntityUser} onClick={onCancel}>
            Cancel
          </Button>

          <Button
            variant="outlined"
            disabled={updatingEntityUser}
            onClick={handleSubmit(handleUpdateEntityAccess)}
          >
            {userEntity ? "Update" : "Add"}
          </Button>
        </AppForm.Actions>
      </AppForm>
    </>
  );
};

export default UserEntityAccessForm;
